import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved, faGavel, faUsers, faTrophy, faHandshake, faPeopleGroup,
  faArrowRightArrowLeft, faCrown, faListOl, faStar, faChartLine, faLock,
} from '@fortawesome/free-solid-svg-icons';
import WebsiteNavbar from '../components/website/WebsiteNavbar';
import WebsiteFooter from '../components/website/WebsiteFooter';
import { useUIStore } from '../stores/uiStore';

const HowToPlayPage: React.FC = () => {
  const openModal = useUIStore((s) => s.openModal);

  const ownerSteps = [
    { icon: faShieldHalved, title: 'Create a Contest', desc: 'Pick a cricket series, name your contest, and choose a plan tier. Free series skip the plan step entirely.' },
    { icon: faGavel, title: 'Configure Rules & Scoring', desc: 'Set team size, player role limits, auction/draft/open selection, transfer windows, captaincy bonus multipliers, and a fully customizable scoring system.' },
    { icon: faUsers, title: 'Publish & Share Code', desc: 'Publish your contest to lock the rules and generate a unique join code. Share it with friends or your community.' },
    { icon: faListOl, title: 'Manage Members', desc: 'Review join requests, accept or reject members, and track teams created in your contest.' },
  ];

  const playerSteps = [
    { icon: faHandshake, title: 'Join with a Code', desc: 'Enter the contest code shared by the owner. Once accepted, you can access the contest.' },
    { icon: faPeopleGroup, title: 'Build Your Dream Team', desc: 'Select players from the series squads within the contest rules. Choose your Captain and Vice-Captain for bonus multipliers.' },
    { icon: faArrowRightArrowLeft, title: 'Make Transfers', desc: 'If the contest has transfers enabled, swap players between matches during active transfer windows.' },
    { icon: faTrophy, title: 'Compete & Climb', desc: 'Earn fantasy points from real match performances. Track your rank on the leaderboard as the series progresses.' },
  ];

  const keyFeatures = [
    { icon: faGavel, title: 'Custom Scoring', desc: 'Contest owners define every scoring rule — runs, wickets, catches, strike rates, economy rates, and bonus/penalty thresholds. No two contests need to score the same way.' },
    { icon: faArrowRightArrowLeft, title: 'Transfer Windows', desc: 'Configure when and how often players can swap squad members — entire series, per match, mid-series, or custom date ranges with transfer limits.' },
    { icon: faCrown, title: 'Captain & Vice-Captain', desc: 'Captain gets a bonus multiplier on their points. Vice-Captain gets a smaller bonus. Owners control the multiplier values and when changes are allowed.' },
    { icon: faLock, title: 'Auto-Locking', desc: 'Teams automatically lock before each match deadline. Lineup snapshots are taken so scoring uses the locked team, not live edits.' },
    { icon: faChartLine, title: 'Live Leaderboard', desc: 'Fantasy points update automatically after each match. Leaderboard tracks total points, match-by-match breakdowns, and rank changes in real time.' },
    { icon: faStar, title: 'Plan Tiers & Add-ons', desc: 'Series can offer free or tiered plans. Higher tiers unlock more features like transfers, auctions, and advanced scoring rules. Or mark a series completely free.' },
  ];

  return (
    <div className="min-h-screen bg-[#0F1114]">
      <WebsiteNavbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-4 text-center overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#13EC5B]/5 rounded-full blur-[120px]" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="inline-block px-3 py-1 bg-[#13EC5B]/10 border border-[#13EC5B]/20 rounded-full text-[#13EC5B] text-xs font-semibold mb-6 uppercase tracking-wider">
            How to Play
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Your Guide to{' '}
            <span className="text-[#13EC5B]">CricClash</span>
          </h1>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Whether you're creating a contest or joining one — here's everything you need to know.
          </p>
        </div>
      </section>

      {/* Two Paths */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-white mb-3">Two Paths, One Game</h2>
            <p className="text-gray-500 max-w-lg mx-auto">CricClash has two roles — contest owners who set the rules, and players who compete within them.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Owner Path */}
            <div>
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-[#13EC5B]/10">
                <div className="w-11 h-11 rounded-xl bg-[#13EC5B]/10 border border-[#13EC5B]/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faShieldHalved} className="text-[#13EC5B] text-lg" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Contest Owner</h3>
                  <p className="text-xs text-gray-500">Create, configure & manage</p>
                </div>
              </div>
              <div className="space-y-6">
                {ownerSteps.map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center shrink-0">
                      <div className="w-9 h-9 rounded-full bg-[#13EC5B]/10 border border-[#13EC5B]/20 flex items-center justify-center">
                        <span className="text-[#13EC5B] text-sm font-bold">{i + 1}</span>
                      </div>
                      {i < ownerSteps.length - 1 && <div className="w-px flex-1 bg-[#13EC5B]/10 mt-2" />}
                    </div>
                    <div className="pb-6">
                      <div className="flex items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={s.icon} className="text-[#13EC5B] text-xs" />
                        <h4 className="text-white font-semibold">{s.title}</h4>
                      </div>
                      <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Player Path */}
            <div>
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-amber-500/10">
                <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faTrophy} className="text-amber-400 text-lg" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Player</h3>
                  <p className="text-xs text-gray-500">Join, build & compete</p>
                </div>
              </div>
              <div className="space-y-6">
                {playerSteps.map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center shrink-0">
                      <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                        <span className="text-amber-400 text-sm font-bold">{i + 1}</span>
                      </div>
                      {i < playerSteps.length - 1 && <div className="w-px flex-1 bg-amber-500/10 mt-2" />}
                    </div>
                    <div className="pb-6">
                      <div className="flex items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={s.icon} className="text-amber-400 text-xs" />
                        <h4 className="text-white font-semibold">{s.title}</h4>
                      </div>
                      <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-4 border-t border-[#2E3340]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-white mb-3">What Makes CricClash Different</h2>
            <p className="text-gray-500 max-w-lg mx-auto">Every contest is unique. Owners control the rules, scoring, and features — nothing is fixed.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {keyFeatures.map((f, i) => (
              <div key={i} className="bg-[#1A1D23] border border-[#2E3340] rounded-2xl p-6 hover:border-[#13EC5B]/20 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-[#13EC5B]/10 border border-[#13EC5B]/20 flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={f.icon} className="text-[#13EC5B]" />
                </div>
                <h3 className="text-white font-bold mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#13EC5B]/10 to-[#00CC6A]/5 border border-[#13EC5B]/20 rounded-3xl p-10 sm:p-14 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Play?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Create your own contest or join a friend's league. It's free to start.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/home"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#13EC5B] text-black rounded-xl font-bold text-sm hover:bg-[#10D450] transition-all"
            >
              <FontAwesomeIcon icon={faShieldHalved} />
              Create a Contest
            </a>
            <button
              onClick={() => openModal('join-contest')}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/5 border border-white/10 text-white rounded-xl font-bold text-sm hover:border-[#13EC5B]/30 hover:text-[#13EC5B] transition-all"
            >
              <FontAwesomeIcon icon={faHandshake} />
              Join a Contest
            </button>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export default HowToPlayPage;
